import React, { useState } from 'react';

import { CoreApi, MasterFilesApi } from '@api';
import { Box, Card, Stack, Text, VStack } from '@chakra-ui/react';
import {
  Field,
  FormController,
  FormLabel,
  formSchemas,
} from '@components/form';
import { useAppSelector } from '@state';
import { CoreType, MasterFilesType } from '@types';
import {
  DataList,
  EmptyStateBox,
  EnvironmentIframe,
  FileUpload,
  OverlayDialog,
  SwitchOnOff,
} from '@ui';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { ATTACHMENT_MAX_FILE_SIZE_BYTES } from '@/constants/uploadLimits';

const formSchema = z.object({
  pdf_file_rev: formSchemas.masterfileSchema.pdfFileSchema as z.ZodType<File>,
});

type FormData = z.infer<typeof formSchema>;

type FrameMasterFile =
  MasterFilesType.GetFrameMasterFilesAll['response'][number];
type PartMasterFile = MasterFilesType.GetPartMasterFilesAll['response'][number];

const checkFileName = (fileName: string, partNumber: string): boolean => {
  if (!fileName || !partNumber) return false;
  if (fileName.length < partNumber.length + 4) return false;

  for (let i = 0; i < partNumber.length; i += 1) {
    if (fileName[i].toLowerCase() !== partNumber[i].toLowerCase()) return false;
  }

  return true;
};

type UpdateProps = {
  type: 'frame' | 'part';
  selectedMasterFile: FrameMasterFile | PartMasterFile | null;
};

const Inner_Update: React.FC<UpdateProps> = ({ selectedMasterFile, type }) => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const [
    postFrameMasterFilesMutation,
    { isLoading: isLoadingPostFrameMasterFile },
  ] = MasterFilesApi.Api.usePostFrameMasterFilesMutation();
  const [
    postPartMasterFilesMutation,
    { isLoading: isLoadingPostPartMasterFile },
  ] = MasterFilesApi.Api.usePostPartMasterFilesMutation();
  const [postAttachmentsPresignedUrlsMutation] =
    CoreApi.Api.usePostAttachmentsPresignedUrlsMutation();

  const [enableEdit, setEnableEdit] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [fileNameCorrect, setFileNameCorrect] = useState(false);

  const isPdfFile = (file: File): boolean => {
    if (!file) return false;
    return (
      file?.type === 'application/pdf' ||
      file?.name?.toLowerCase()?.endsWith('.pdf')
    );
  };

  const handlePdfFileChange = (file: File) => {
    if (!file || !selectedMasterFile) return;
    setSelectedPdfFile(file);
    if (type === 'frame') {
      if ('frame' in selectedMasterFile) {
        setFileNameCorrect(checkFileName(file.name, selectedMasterFile?.frame));
      }
    } else {
      if ('part_number' in selectedMasterFile) {
        setFileNameCorrect(
          checkFileName(file.name, selectedMasterFile?.part_number),
        );
      }
    }
  };

  const onFormSubmit = async (formData: FormData) => {
    OverlayDialog.open('confirmMasterfileUpdate', {
      overlayId: 'confirmMasterfileUpdate',
      title: 'Confirm Masterfile Update',
      description: 'Are you sure you want to update the masterfiles?',
      content: (
        <VStack align="flex-start" gap={2} mt={3}>
          <DataList
            items={[
              {
                label: 'Project',
                value: selectedProject.item?.name,
              },
              {
                label: 'Master File',
                value: selectedMasterFile?.id as number,
              },
              ...(type === 'frame' &&
              selectedMasterFile &&
              'frame' in selectedMasterFile
                ? [
                    {
                      label: 'Frame Number',
                      value: selectedMasterFile.frame,
                    },
                  ]
                : []),
              ...(type === 'part' &&
              selectedMasterFile &&
              'part_number' in selectedMasterFile
                ? [
                    {
                      label: 'Part Number',
                      value: selectedMasterFile.part_number,
                    },
                  ]
                : []),
              {
                label: 'Current Revision',
                value: selectedMasterFile?.active_revision || 'Nil',
              },
              {
                label: 'New Revision File',
                value:
                  formData.pdf_file_rev && formData.pdf_file_rev instanceof File
                    ? formData.pdf_file_rev.name
                    : 'No file',
              },
            ]}
            orientation="horizontal"
            divideY="1px"
          />
        </VStack>
      ),
      handleConfirm: async () => {
        const file = formData.pdf_file_rev as File;
        if (!file) return;

        const getPresignedUrlsPayload: CoreType.PostAttachmentsPresignedUrls['body'][number] =
          {
            module: type === 'frame' ? 'frame_masterfiles' : 'part_masterfiles',
            filename: getFileNameWithoutExtension(file.name),
            content_type: file.type,
            size: file.size,
          };

        const getPresignedUrls = await postAttachmentsPresignedUrlsMutation({
          body: [getPresignedUrlsPayload],
        }).unwrap();

        const res: MasterFilesType.PostFrameMasterFiles['body'][number] =
          await fetch(getPresignedUrls?.[0]?.upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          }).then(() => ({
            filename: getPresignedUrls?.[0]?.filename,
            file_url: getPresignedUrls?.[0]?.file_url,
          }));

        if (!res.file_url) throw new Error(`Upload failed for ${file.name}`);

        const { data, error } =
          type === 'frame'
            ? await postFrameMasterFilesMutation({
                params: {
                  project_id: selectedProject.id!,
                },
                body: [res],
              })
            : await postPartMasterFilesMutation({
                params: {
                  project_id: selectedProject.id!,
                },
                body: [res],
              });

        if (data && !error) {
          OverlayDialog.close('confirmMasterfileUpdate');
          setEnableEdit(false);
          setFileNameCorrect(false);
          setSelectedPdfFile(null);
        }
      },
    });
  };

  return (
    <>
      <Stack gap={2}>
        <SwitchOnOff
          label="Enable Edit"
          infoTip="Enable edit mode to update the masterfiles"
          value={enableEdit}
          onValueChange={setEnableEdit}
          disabled={!selectedMasterFile}
        />

        <Card.Root
          width="100%"
          variant="elevated"
          flexDirection="row"
          key={`mf-edit-${selectedMasterFile?.id}-${selectedPdfFile?.name}-${isLoadingPostFrameMasterFile}-${isLoadingPostPartMasterFile}`}
        >
          <Card.Body flexDirection="column" gap="5">
            {!selectedMasterFile ? (
              <EmptyStateBox
                title="No Masterfile Selected"
                description="Please select a masterfile from the table to update"
              />
            ) : !enableEdit ? (
              <EmptyStateBox
                title="Update Mode is not enabled"
                description="Please enable edit mode to update the masterfiles"
              />
            ) : (
              <Stack gap={2} width="full" px={{ base: 5, lg: 10 }}>
                <FormController
                  label="Update Masterfiles"
                  helperText={
                    !selectedMasterFile
                      ? 'Please select a masterfile'
                      : 'Please fill out the form to update the masterfiles'
                  }
                  formSchema={formSchema as z.ZodType<any>}
                  onFormSubmit={onFormSubmit}
                  disabled={!selectedMasterFile}
                  defaultValues={{
                    pdf_file_rev: selectedPdfFile?.name
                      ? selectedPdfFile
                      : undefined,
                  }}
                  buttonProps={{
                    variant: 'outline',
                    color: 'tertiary.fg',
                    width: '100%',
                    mt: '4',
                  }}
                >
                  {({ register, control, errors, disabled }) => {
                    return (
                      <>
                        <Stack
                          flexDirection={'column'}
                          alignItems="flex-start"
                          gap={2}
                          _disabled={
                            disabled
                              ? {
                                  opacity: 0.5,
                                  cursor: 'not-allowed',
                                }
                              : {}
                          }
                        >
                          <FormLabel label="Project">
                            {selectedProject.item?.name}
                          </FormLabel>
                          <FormLabel label={`Masterfile`}>
                            {selectedMasterFile?.id as number}
                          </FormLabel>
                          <FormLabel
                            label={`${type === 'frame' ? 'Frame' : 'Part'} Number`}
                          >
                            {'frame' in selectedMasterFile
                              ? selectedMasterFile?.frame
                              : 'part_number' in selectedMasterFile
                                ? selectedMasterFile?.part_number
                                : ''}
                          </FormLabel>
                          <FormLabel label={'Current Revision'}>
                            {selectedMasterFile?.active_revision || (
                              <Text color={'red'}>Nil</Text>
                            )}
                          </FormLabel>

                          <Field
                            label="Upload New Revision"
                            invalid={!!errors?.['pdf_file_rev']}
                            errorText={
                              errors?.['pdf_file_rev']?.message as string
                            }
                            disabled={disabled}
                            required
                          >
                            <Controller
                              control={control}
                              name="pdf_file_rev"
                              render={({ field }) => (
                                <FileUpload
                                  key={`upload-file-${selectedMasterFile?.id}-${selectedPdfFile?.name}`}
                                  label="Upload New Revision"
                                  id="pdf_file_rev"
                                  register={register}
                                  control={control}
                                  errors={errors}
                                  disabled={disabled}
                                  maxFileSize={ATTACHMENT_MAX_FILE_SIZE_BYTES}
                                  minFiles={1}
                                  maxFiles={1}
                                  fileType="pdf"
                                  customFilter={(file: File) => {
                                    if (
                                      !checkFileName(
                                        file.name,
                                        'frame' in selectedMasterFile
                                          ? selectedMasterFile?.frame
                                          : 'part_number' in selectedMasterFile
                                            ? selectedMasterFile?.part_number
                                            : '',
                                      )
                                    )
                                      return {
                                        code: 'FILE_NAME_MISMATCH',
                                        message:
                                          'File name must match the part number.',
                                      };
                                  }}
                                  helperText={
                                    'Upload a new revision of the masterfile. This will overwrite the existing file. Please ensure that the revision name is same as the part number.'
                                  }
                                  onUpload={(files) => {
                                    field.onChange(files);
                                    handlePdfFileChange(
                                      Array.isArray(files) ? files[0] : files,
                                    );
                                  }}
                                />
                              )}
                            />
                          </Field>
                        </Stack>
                      </>
                    );
                  }}
                </FormController>
                {selectedPdfFile &&
                  isPdfFile(selectedPdfFile) &&
                  fileNameCorrect && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      width="100%"
                      height="65vh"
                      alignItems="center"
                      justifyContent="center"
                      mt={4}
                      gap={4}
                    >
                      <Text textStyle="heading.card" color="secondary.fg">
                        <i>File Preview</i>
                      </Text>
                      <EnvironmentIframe
                        key={selectedPdfFile.name}
                        allowFullScreen
                        loading="eager"
                        src={URL.createObjectURL(selectedPdfFile)}
                        title={selectedPdfFile.name}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                      />
                    </Box>
                  )}
              </Stack>
            )}
          </Card.Body>
        </Card.Root>
      </Stack>
    </>
  );
};

const Update: React.FC<UpdateProps> = ({ selectedMasterFile, type }) => {
  return (
    <>
      <Inner_Update selectedMasterFile={selectedMasterFile} type={type} />
    </>
  );
};

export default Update;

const getFileNameWithoutExtension = (filename: string) =>
  filename.slice(0, filename.lastIndexOf('.')) || filename;
